import { UserOptionLabel } from "@/components";
import { DEFAULT_DEBOUNCE_TIME } from "@/config";
import { useGetUsersByIdsMutation, useSuggestUsersQuery } from "@/services";
import { Button, Select, Space, Spin } from "antd";
import debounce from "lodash.debounce";
import { array, func, object, oneOfType, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { FilterDropdown, FilterWrapper } from "../styled";

const { Option } = Select;

export const TableUserFilter = ({
  inputRef,
  onSubmit,
  onReset,
  confirm,
  queryParamValue: userIds,
}) => {
  const intl = useIntl();

  const [filterTerm, setFilterTerm] = useState();
  const [filteredUsers, setFilteredUsers] = useState();

  const { data: userSuggestions, isFetching: fetchingSuggestions } =
    useSuggestUsersQuery(
      { query: filterTerm },
      {
        skip: !filterTerm,
      }
    );
  const [getUsersByIds, { data: users, isFetching: fetchingSuggestionsByIds }] =
    useGetUsersByIdsMutation();

  const isFetching = fetchingSuggestions || fetchingSuggestionsByIds;

  const userIdsKey = Array.isArray(userIds)
    ? [...new Set(userIds)].join(",")
    : userIds || "";

  useEffect(() => {
    if (!userIdsKey) return;

    const ids = userIdsKey.includes(",")
      ? userIdsKey.split(",")
      : [userIdsKey];

    getUsersByIds(ids);
  }, [getUsersByIds, userIdsKey]);

  useEffect(() => {
    if (!users) return;

    const options = users.map(({ id, displayName }) => ({
      value: id,
      label: displayName,
    }));

    setFilteredUsers(options);
  }, [users]);

  const handleChange = (selectedValue) => {
    setFilteredUsers(selectedValue);
    onSubmit(selectedValue);
  };

  const handleReset = () => {
    setFilterTerm();

    confirm();
    onReset();
  };

  const onSearch = debounce((searchTerm) => {
    setFilterTerm(searchTerm);
  }, DEFAULT_DEBOUNCE_TIME);

  return (
    <FilterWrapper>
      <FilterDropdown
        ref={inputRef}
        mode="multiple"
        showSearch
        defaultActiveFirstOption={false}
        optionLabelProp="label"
        filterOption={false}
        allowClear
        data-test-id="filter-dropdown"
        placeholder={intl.formatMessage({
          id: "tableFilters.dropDownPlaceholder",
        })}
        value={filteredUsers}
        onSearch={onSearch}
        onChange={handleChange}
        notFoundContent={isFetching ? <Spin size="small" /> : null}
      >
        {userSuggestions?.map(
          ({ document: { id, displayName, department, email, jobTitle } }) => (
            <Option key={id} label={displayName}>
              <UserOptionLabel
                displayName={displayName}
                department={department}
                email={email}
                jobTitle={jobTitle}
              />
            </Option>
          )
        )}
      </FilterDropdown>
      <Space>
        <Button data-test-id="reset-button" onClick={handleReset} size="small">
          {intl.formatMessage({ id: "general.reset" })}
        </Button>
      </Space>
    </FilterWrapper>
  );
};

TableUserFilter.propTypes = {
  inputRef: object,
  onSearch: func,
  onReset: func,
  onSubmit: func,
  confirm: func,
  queryParamValue: oneOfType([string, array]),
};
